import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@lib/utils'

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Checkbox label */
  label?: string
  /** Description below label */
  description?: string
  /** Indeterminate state */
  indeterminate?: boolean
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Checkbox component with brutalist styling
 *
 * @example
 * <Checkbox label="Accept terms" />
 * <Checkbox label="Select all" indeterminate />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      indeterminate = false,
      size = 'md',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).slice(2, 9)}`

    return (
      <div className={cn('flex items-start gap-3', className)}>
        <div className="relative flex items-center">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              // Base styles
              'flex items-center justify-center border-2 border-border bg-surface',
              'transition-all cursor-pointer',
              'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',

              // Size
              size === 'sm' && 'h-4 w-4',
              size === 'md' && 'h-5 w-5',
              size === 'lg' && 'h-6 w-6',

              // Checked state
              'peer-checked:bg-primary peer-checked:border-primary',

              // Disabled state
              'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',

              // Hover (when not disabled)
              'peer-enabled:hover:border-primary'
            )}
            onClick={() => {
              if (!disabled) {
                document.getElementById(checkboxId)?.click()
              }
            }}
          >
            {/* Checkmark */}
            <svg
              className={cn(
                'text-text-inverse opacity-0 transition-opacity',
                'peer-checked:opacity-100',
                size === 'sm' && 'h-3 w-3',
                size === 'md' && 'h-3.5 w-3.5',
                size === 'lg' && 'h-4 w-4'
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              {indeterminate ? (
                <line x1="5" y1="12" x2="19" y2="12" />
              ) : (
                <polyline points="20 6 9 17 4 12" />
              )}
            </svg>
          </div>
        </div>

        {(label || description) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={checkboxId}
                className={cn(
                  'text-text-primary cursor-pointer select-none',
                  size === 'sm' && 'text-body-sm',
                  size === 'md' && 'text-body',
                  size === 'lg' && 'text-body-lg',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-caption text-text-muted mt-0.5">{description}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

/**
 * Checkbox option type for groups
 */
export interface CheckboxOption {
  value: string
  label: string
  description?: string
}

/**
 * Checkbox group for multiple options
 */
export interface CheckboxGroupProps {
  /** Group label */
  label?: string
  /** Checkbox options */
  options: CheckboxOption[]
  /** Selected values */
  value?: string[]
  /** Change handler */
  onChange?: (values: string[]) => void
  /** Orientation */
  orientation?: 'horizontal' | 'vertical'
  /** Size */
  size?: 'sm' | 'md' | 'lg'
  /** Disabled state */
  disabled?: boolean
}

export function CheckboxGroup({
  label,
  options,
  value = [],
  onChange,
  orientation = 'vertical',
  size = 'md',
  disabled,
}: CheckboxGroupProps) {
  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange?.([...value, optionValue])
    } else {
      onChange?.(value.filter((v) => v !== optionValue))
    }
  }

  return (
    <fieldset className="space-y-3">
      {label && (
        <legend className="text-body-sm font-medium text-text-primary mb-2">
          {label}
        </legend>
      )}
      <div
        className={cn(
          'flex gap-4',
          orientation === 'vertical' && 'flex-col',
          orientation === 'horizontal' && 'flex-row flex-wrap'
        )}
      >
        {options.map((option) => (
          <Checkbox
            key={option.value}
            label={option.label}
            description={option.description}
            checked={value.includes(option.value)}
            onChange={(e) => handleChange(option.value, e.target.checked)}
            size={size}
            disabled={disabled}
          />
        ))}
      </div>
    </fieldset>
  )
}

export default Checkbox
